import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, flatMap, map, share } from 'rxjs/operators';
import { GORM_IDENTITY_SETTINGS, IGormIdentitySettings } from './gorm-identity.module';
import { UserService } from 'services/user.service';


@Injectable() export class GormIdentityInterceptor implements HttpInterceptor {

	private token?: string;
	private tokenUrl = `${environment.gatewayUrl}/api/token`;
	private tokenRequest?: Observable<string>;

	constructor(private readonly http: HttpClient, @Inject(GORM_IDENTITY_SETTINGS) private readonly settings: IGormIdentitySettings, userService: UserService) {
		const storedTokenString = localStorage.getItem('gorm.user-token');
		if (!storedTokenString) return;

		const storedToken: { token: string, expires: string; } = JSON.parse(storedTokenString);

		if (new Date(parseInt(storedToken.expires)) <= new Date()) localStorage.removeItem('gorm.user-token');
		else this.token = storedToken.token;

		userService.onCredentialsChanged().subscribe(() => {
			this.token = undefined; // Force reload of token to ensure new credentials are passed to backend requests
		});
	}


	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		// Token URL is the only request we will never require a Gorm Identity Token for
		if (req.url === this.settings.tokenUrl) return next.handle(req);

		// Also require tokens only for URLs matching the supplied patterns
		if (this.settings.matchUrls.filter(matchUrl => req.url.startsWith(matchUrl)).length === 0) return next.handle(req);

		// Ensure token is only requested once per session
		if (this.token) return this.addToken(req, next, this.token);

		// If we don't have a token yet, get it from Identity service
		return this.ensureTokenRequestAndAddToken(req, next);
	}

	ensureTokenRequestAndAddToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		if (!this.tokenRequest) {
			// Prevent multiple concurrent requests to get the same data
			this.tokenRequest = this.http.get(this.tokenUrl, { responseType: 'text' }).pipe(share());
		}

		return this.tokenRequest!.pipe(
			flatMap(token => {
				this.tokenRequest = undefined;
				// Store token so it's not necessary to fetch it on next load
				localStorage.setItem('gorm.user-token', JSON.stringify({
					token: token,
					expires: ((new Date()).getTime() + 60000 * 60 * 16) // Token always expires in 24 hours, so assuming 16 is fine, this is just for our localstorage cache
				}));
				return this.addToken(req, next, token);
			}),
			catchError((response: HttpErrorResponse, caught) => {
				if (response.status === 404) {
					throw 'User not found. Unable to log in.';
				}
				throw response;
			})
		);
	}

	addToken(req: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {

		this.token = token;
		req = req.clone({
			withCredentials: true,
			headers: req.headers.set('x-gorm-token', token)
		});
		return next.handle(req).pipe(
			map(event => {
				if (event instanceof HttpResponse) {
					const credentials = event.headers.get('X-Gorm-Credentials') as string;
					if (credentials) console?.log(`Required credentials: %c${credentials}`, 'color: blue; font-weight: bold', `(/api/${req.url.split('/api/')[1]})`);
				}
				return event;
			}),
			catchError((response: HttpErrorResponse | Error) => {
				if (response instanceof Error) return throwError(() => response);
				const credentials = response.headers.get('X-Gorm-Credentials') as string;
				const error = response.headers.get('X-Gorm-Token-Error') as string;
				const expired = response.headers.get('X-Gorm-Token-Expired')?.toLowerCase() == 'true';

				if (credentials) console?.log(`Required credentials: %c${credentials}`, 'color: red; font-weight: bold', `(/api/${req.url.split('/api/')[1]})`);
				if (expired) return this.ensureTokenRequestAndAddToken(req, next); // Retry with a new token
				if (error) return throwError(() => error);
				return throwError(() => response);
			}))
	}
}
